<template>
    <central-modal
        :header="modalHeader"
        v-model:visible="isVisible"
        :size="{ width: '50vw', height: '65vh' }"
        @hide="onModalHide"
    >
        <div class="category-modal-content">
            <info-banner severity="info" role="note">Category changes are saved when you save the pool.</info-banner>
            <div class="field-row">
                <label for="categoryName">Category name (optional)</label>
                <input-text
                    input-id="categoryName"
                    v-model="categoryName"
                    :maxlength="50"
                    placeholder="e.g. Nearby batteries"
                />
            </div>
            <gb-index-fields
                :read-only="false"
                :editor-type="CustomIndexEditorEnum.add"
                :index-settings="indexFilters"
                @update:index="updateIndexFilters"
                @update:validation="fieldsValidation = $event"
            />
        </div>
        <template #footer>
            <div class="footer-wrapper">
                <primevue-button
                    :disabled="!!fieldsValidation"
                    v-tooltip.top="fieldsValidation"
                    @click="saveCategory"
                >
                    Apply category
                    <font-awesome-icon :icon="['fas', 'check']" />
                </primevue-button>
                <primevue-button class="plain-btn" @click="closeModal">
                    Cancel
                    <font-awesome-icon :icon="['fas', 'xmark']" />
                </primevue-button>
            </div>
        </template>
    </central-modal>
</template>

<script lang="ts" setup>
import {
    computed, onBeforeUnmount, ref, watch,
} from 'vue';
import InfoBanner from '@/components/common/InfoBanner.vue';
import CentralModal from '@/components/common/CentralModal.vue';
import PrimevueButton from 'primevue/button';
import InputText from 'primevue/inputtext';
import GbIndexFields from '@/components/benchmarks/index/GbIndexFields.vue';
import { CustomIndexEditorEnum } from '@/enums/benchmarking/IndexEnums.ts';
import { DEFAULT_GB_INDEX_FIELDS } from '@/constants/benchmarks/IndexConstants.ts';
import { IGbIndex } from '@/interfaces/benchmarks/index/IIndex.ts';
import { ILeaderboardBenchmarkPoolCategory } from '@/interfaces/powerMarkets/leaderboard/IBenchmarkPool.ts';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import { cloneDeep } from '@/utils/global.ts';

const centralModalStore = useCentralModalStore();

const isVisible = defineModel<boolean>('visible', { required: true });

const props = defineProps<{
    mode: 'add' | 'edit';
    category: ILeaderboardBenchmarkPoolCategory | null;
}>();

const emit = defineEmits<{
    save: [ILeaderboardBenchmarkPoolCategory];
}>();

const categoryName = ref('');
const indexFilters = ref<IGbIndex>(cloneDeep(DEFAULT_GB_INDEX_FIELDS));
const fieldsValidation = ref<string | null>(null);
const editingCategoryId = ref<string | null>(null);

const modalHeader = computed(() => (props.mode === 'add' ? 'Add category' : 'Edit category'));

function syncFromProps (): void {
    if (props.mode === 'edit' && props.category) {
        editingCategoryId.value = props.category.id;
        categoryName.value = props.category.name ?? '';
        indexFilters.value = cloneDeep(props.category.indexFilters);
        return;
    }
    editingCategoryId.value = null;
    categoryName.value = '';
    indexFilters.value = cloneDeep(DEFAULT_GB_INDEX_FIELDS);
}

watch(() => [isVisible.value, props.mode, props.category?.id] as const, ([visible]) => {
    if (!visible) return;
    syncFromProps();
    fieldsValidation.value = null;
}, { immediate: true });

function updateIndexFilters (value: IGbIndex): void {
    indexFilters.value = value;
}

function closeModal (): void {
    centralModalStore.closeCurrentModal();
}

function saveCategory (): void {
    if (fieldsValidation.value) return;

    const category: ILeaderboardBenchmarkPoolCategory = {
        ...(props.mode === 'edit' ? props.category : {}),
        id: editingCategoryId.value ?? crypto.randomUUID(),
        name: categoryName.value.trim() || undefined,
        indexFilters: cloneDeep(indexFilters.value),
    };
    emit('save', category);
    closeModal();
}

function onModalHide (): void {
    fieldsValidation.value = null;
}

onBeforeUnmount(centralModalStore.bindNestedModalCloseGuard(isVisible));
</script>

<style lang="scss" scoped>
.category-modal-content {
    padding: var(--largeSpacing);
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
    overflow-y: auto;
    min-height: 0;
}

.field-row {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);

    label {
        font-weight: var(--boldFontWeight);
    }
}

.footer-wrapper {
    display: flex;
    gap: var(--defaultSpacing);
    justify-content: flex-end;
    width: 100%;
}
</style>
